import { useCallback, useEffect, useState } from "react";

import { Routes, Route } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import { useAuth } from "react-oidc-context";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { defaultTheme } from "./styles/Theme";

import HomePage from "./pages/home";
import NoMatchPage from "./pages/noMatch";

import "./App.css";
import NitroService from "./utils/nitro.service";
import { useNitroStore } from "./store/Store";
import { ICacheProduct } from "./store/cacheSlice";
import EditingSnacks from "./components/editingSnacks";
import { useIsComponentMounted } from "./hooks/useIsComponentMounted";
import { IFeatureFlags } from "./types/IFeatureFlags";

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const isComponentMounted = useIsComponentMounted();

  const auth = useAuth();
  const ldClient = useLDClient();
  const flags = useFlags<IFeatureFlags>();
  const [setFeatureFlags] = useNitroStore((state) => [state.setFeatureFlags]);

  useEffect(() => {
    const identifyUser = async () => {
      if (!ldClient) {
        return;
      }
      const email =
        typeof auth.user?.profile?.username === "string"
          ? auth.user.profile.username.split("_").pop()
          : "default-email";
      try {
        const context = {
          kind: "user",
          key: email ?? "default-key",
          name: auth.user?.profile?.name ?? "Anonymous",
          email: email ?? "anonymous@example.com",
        };

        await ldClient.identify(context);
      } catch (error) {
        console.error("Error identifying user with LaunchDarkly:", error);
      }
    };

    identifyUser();
  }, [auth, ldClient]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      setFeatureFlags({
        manageRailRate: flags.manageRailRate ?? true,
        originManager: flags.originManager ?? true,
        multiRailStation: flags.multiRailStation ?? true,
      });
    }
  }, [auth.isAuthenticated, flags, setFeatureFlags]);

  const [products, setProducts, priorityProducts] = useNitroStore((state) => [
    state.products,
    state.setProducts,
    state.priorityProducts,
  ]);

  const loadProducts = useCallback(async () => {
    try {
      if (products.size > 0) return;
      const result = await NitroService.getProducts();

      const productMap = new Map<string, ICacheProduct>();
      productMap.set("", {
        id: "",
        name: "",
        carType: "",
        stccCodes: [],
        traticsLoadedWeight: 0,
        traticsWeightType: 0,
        railCarUom: "",
        label: "",
        value: "",
        group: "ALL",
      });
      result
        .map((r) => ({
          ...r,
          label: r.name,
          value: r.name,
          group: priorityProducts.indexOf(r.name) > -1 ? "Favorites" : "Others",
        }))
        .sort((a, b) => {
          if (a.group === b.group) return 0;
          return a.group === "Favorites" ? -1 : 1;
        })
        .forEach((r) => productMap.set(r.name, r));

      setProducts(productMap);
    } catch (err) {
      console.error(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityProducts, setProducts]);

  const load = useCallback(async () => {
    setIsLoading(true);
    await loadProducts();
    setIsLoading(false);
  }, [loadProducts]);

  useEffect(() => {
    if (!isComponentMounted) return;
    load();
  }, [isComponentMounted, load]);

  if (isLoading) {
    return <div />;
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <EditingSnacks />
      <Routes>
        <Route index element={<HomePage />} />
        <Route path="*" element={<NoMatchPage />} />
      </Routes>
    </ThemeProvider>
  );
};

export default App;
